/*
 * Copyright (c) 2017-2024. Code réalisé par IpsoSenso.
 */

import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import PropTypes from 'prop-types'
import React, { useContext, useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { connect } from 'react-redux'
import { compose } from 'redux'
import { Field, formValueSelector, reduxForm } from 'redux-form'
import Grid from '@material-ui/core/Grid'
import { FormInput, FormSelect, MultipleChoice } from 'isotope-client'
import { PhidemDataContext } from '../../common/phidemData/PhidemDataContext'
import MenuItem from '@material-ui/core/MenuItem'
import { renderRoleWithCenterName } from './services/userUtils'
import { UserRoles } from './services/userEnums'

const validate = (values, { isEdition, intl }) => {
	const errors = {}

	if (!values.lastname) {
		errors.lastname = intl.formatMessage({ id: 'global.error.validation.required' })
	}

	if (!values.firstname) {
		errors.firstname = intl.formatMessage({ id: 'global.error.validation.required' })
	}

	if (!values.zoneId && !values.roles?.map(role => role.code)?.includes(UserRoles.ADMIN)) {
		errors.zoneId = intl.formatMessage({ id: 'global.error.validation.required' })
	}

	if (!values.login) {
		errors.login = intl.formatMessage({ id: 'global.error.validation.required' })
	}

	if (!values.email) {
		errors.email = intl.formatMessage({ id: 'global.error.validation.required' })
	}

	if (!isEdition && !values.password) {
		errors.password = intl.formatMessage({ id: 'global.error.validation.required' })
	}

	if (!isEdition && !values.passwordRepeated) {
		errors.passwordRepeated = intl.formatMessage({ id: 'global.error.validation.required' })
	}

	return errors
}

const UserForm = (
	{
		isEdition,
		roles,
		handleSubmit,
		onSubmit,
		onClose,
		userId,
		user,
		rolesForm,
		intl,
		change
	}
) => {
	const { zones, user: { isSuperAdmin, selectedZone } } = useContext(PhidemDataContext)

	useEffect(() => {
		if (rolesForm?.includes(UserRoles.ADMIN) && rolesForm?.length > 0) {
			change('zoneId', null)
		}
	}, [rolesForm, change])

	return (
		<Dialog
			disableBackdropClick
			disableEscapeKeyDown
			open
			maxWidth="sm"
			fullWidth
		>
			<DialogTitle>
				<FormattedMessage
					id={`users.form.title${isEdition ? 'Update' : 'Create'}`}
					defaultMessage={isEdition ? 'Modifier un utilisateur' : 'Ajout d\'un utilisateur'}
					description="Dialog title"
				/>
			</DialogTitle>
			<DialogContent>
				<form
					autoComplete="off"
					onSubmit={handleSubmit(onSubmit)}
				>
					<Grid item container xs={12}>
						<Grid item xs={6}>
							<Field
								name="lastname"
								required
								label={
									<FormattedMessage
										id="users.lastname"
										defaultMessage="Nom"
										description="Lastname"
									/>
								}
								fullWidth
								component={FormInput}
							/>
						</Grid>
						<Grid item xs={6}>
							<Field
								name="firstname"
								required
								label={
									<FormattedMessage
										id="users.firstname"
										defaultMessage="Prénom"
										description="Firstname"
									/>
								}
								fullWidth
								component={FormInput}
							/>
						</Grid>
						<Grid item xs={6}>
							<Field
								name="login"
								required
								label={
									<FormattedMessage
										id="users.login"
										defaultMessage="Identifiant"
										description="Login"
									/>
								}
								fullWidth
								component={FormInput}
							/>
						</Grid>
						<Grid item xs={6}>
							<Field
								name="email"
								required
								label={
									<FormattedMessage
										id="users.email"
										defaultMessage="Mail"
										description="Email"
									/>
								}
								fullWidth
								component={FormInput}
							/>
						</Grid>
						<Grid item container xs={6}>
							{isSuperAdmin &&
								<Grid item xs={11}>
									<Field
										name="zoneId"
										component={FormSelect}
										required={!rolesForm?.includes(UserRoles.ADMIN)}
										disabled={user?.id === userId || rolesForm?.includes(UserRoles.ADMIN)}
										label={
											<FormattedMessage
												id="product.Zone"
												defaultMessage="Zone"
												description="Zone"
											/>
										}
									>
										{zones.map((zone) => (
											<MenuItem key={zone.id} value={zone.id}>
												{zone.name}
											</MenuItem>
										))}
									</Field>
								</Grid>
							}
						</Grid>
						<Grid item container xs={6} />
						<Grid item xs={6}>
							<Field
								name="password"
								required={!isEdition}
								helperText="Laissez vide pour ne pas mettre à jour"
								label={
									<FormattedMessage
										id="users.password"
										defaultMessage="Mot de passe"
										description="Password"
									/>
								}
								type="password"
								fullWidth
								component={FormInput}
							/>
						</Grid>
						<Grid item xs={6}>
							<Field
								name="passwordRepeated"
								required={!isEdition}
								label={
									<FormattedMessage
										id="users.passwordConfirm"
										defaultMessage="Confirmation du mot de passe"
										description="Password confirm"
									/>
								}
								type="password"
								fullWidth
								component={FormInput}
							/>
						</Grid>
						<Grid item xs={6}>
							<Field
								name="phoneNumber"
								label={
									<FormattedMessage
										id="users.phoneNumber"
										defaultMessage="Téléphone"
										description="Phone number"
									/>
								}
								fullWidth
								component={FormInput}
							/>
						</Grid>
						<Grid item xs={6}>
							<Field
								name="company"
								label={
									<FormattedMessage
										id="users.company"
										defaultMessage="Société"
										description="Company"
									/>
								}
								fullWidth
								component={FormInput}
							/>
						</Grid>
						<Field
							name="roles"
							label={
								<FormattedMessage
									id="users.profile"
									defaultMessage="Profil"
									description="Profile"
								/>
							}
							disabled={user?.id === userId && !isSuperAdmin}
							fullWidth
							choices={roles}
							getLabelElement={role => renderRoleWithCenterName(role.code, intl, selectedZone)}
							component={MultipleChoice}
						>
						</Field>
					</Grid>
				</form>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} color="primary" variant="outlined">
					<FormattedMessage
						id="btn.cancel"
						defaultMessage="Annuler"
						description="Cancel button"
					/>
				</Button>
				<Button onClick={handleSubmit(onSubmit)} color="primary" variant="contained">
					<FormattedMessage
						id="btn.validate"
						defaultMessage="Valider"
						description="Validate button"
					/>
				</Button>
			</DialogActions>
		</Dialog>
	)

}

UserForm.propTypes = {
	isEdition: PropTypes.bool,
	roles: PropTypes.array.isRequired,
	handleSubmit: PropTypes.func.isRequired,
	onSubmit: PropTypes.func.isRequired,
	onClose: PropTypes.func.isRequired
}

const mapStateToProps = (state, { roles, user, selectedZone }) => {
	const selectedUser = user || {}

	const selector = formValueSelector('UserForm')
	const rolesForm = selector(state, 'roles')

	return {
		userId: state.user.id,
		rolesForm: rolesForm?.map(role => role.code),
		roles,
		initialValues: {
			id: selectedUser.id || null,
			login: selectedUser.login || null,
			lastname: selectedUser.lastname || null,
			firstname: selectedUser.firstname || null,
			email: selectedUser.email || null,
			phoneNumber: selectedUser.phoneNumber || null,
			company: selectedUser.company || null,
			roles: selectedUser.roles || [],
			zoneId: selectedUser.zoneId || selectedZone?.id || null
		}
	}
}

export default compose(
	connect(mapStateToProps),
	reduxForm({
		form: 'UserForm',
		validate
	})
)(UserForm)

